/**
 * Header navigation.
 *
 * The Work menu is derived from `configPortfolio` rather than restated
 * here. It previously listed "Bulletin" under Other projects — a project
 * that is not in the portfolio — and repeated the Dialux and Probuddy
 * descriptions, so the two could drift apart silently.
 *
 * Imagery uses each project's `menuImage` (640px). The full-size covers are
 * ~1600px and were being downscaled to ~292px in the panel, which is what
 * made the menu thumbnails look soft.
 */
import { configPortfolio } from './configPortfolio';
import { servicePagePath } from './configServicePages';

/** Cards shown with imagery; the rest become the "Other projects" list. */
const FEATURED_COUNT = 2;

const featuredProjects = configPortfolio.slice(0, FEATURED_COUNT);
const otherProjects = configPortfolio.slice(FEATURED_COUNT);

export const configNav = [
  {
    id: 'services',
    label: 'Services',
    config: {
      layout: 'list',
      columns: 3,
      /*
       * Sub-services now carry a real `link` to the service page that covers
       * them, derived from the slug so an href cannot drift from a route.
       * Several items share a page — four design items all lead to UI/UX —
       * which is correct: the menu is a finer taxonomy than the eight
       * services are.
       *
       * Every item in this menu now has a real destination: the service
       * groups point at service pages, and Cooperation Models points at the
       * engagement cards on the landing page. The fallback is therefore
       * unused, and kept only so a newly added item cannot render as a dead
       * span — pointing at the hub rather than at a section anchor.
       */
      fallbackLink: '/services',
      aside: {
        id: 'allServices',
        link: {
          label: 'All services',
          /* The hub page, not the landing page's section anchor. */
          link: '/services',
        },
        title: 'Explore our services',
        description:
          'Discover how our technical expertise can help you design, build, and scale your product.',
        image: configPortfolio[0].menuImage,
        imageAlt: 'gitribe',
      },
      navConfig: [
        {
          id: 'ideation',
          label: 'Ideation',
          config: [
            {
              id: 'prototyping',
              label: 'Rapid Prototyping',
              link: servicePagePath('customSoftwareDevelopment'),
            },
            {
              id: 'rnd',
              label: 'Research & Development',
              link: servicePagePath('customSoftwareDevelopment'),
            },
            {
              id: 'userResearchAndTesting',
              label: 'User Research & Testing',
              link: servicePagePath('uiUxDesign'),
            },
            {
              id: 'productStrategy',
              label: 'Product Strategy',
              link: servicePagePath('customSoftwareDevelopment'),
            },
          ],
        },
        {
          id: 'design',
          label: 'Design',
          config: [
            {
              id: 'productDesign',
              label: 'Product Design',
              link: servicePagePath('uiUxDesign'),
            },
            {
              id: 'ux',
              label: 'UX Design',
              link: servicePagePath('uiUxDesign'),
            },
            {
              id: 'ui',
              label: 'UI Design',
              link: servicePagePath('uiUxDesign'),
            },
            {
              id: 'designSystems',
              label: 'Design Systems',
              link: servicePagePath('uiUxDesign'),
            },
          ],
        },
        {
          id: 'softwareDevelopment',
          label: 'Software development',
          config: [
            {
              id: 'web',
              label: 'Web Development',
              link: servicePagePath('webDevelopment'),
            },
            {
              id: 'mobile',
              label: 'Mobile Development',
              link: servicePagePath('mobileAppDevelopment'),
            },
            {
              id: 'mvps',
              label: 'MVPs',
              link: servicePagePath('customSoftwareDevelopment'),
            },
            {
              id: 'cloud',
              label: 'Cloud Strategy',
              link: servicePagePath('cloudSolutions'),
            },
          ],
        },
        {
          id: 'generativeAIAndData',
          label: 'Generative AI and Data',
          config: [
            {
              id: 'AIDevelopment',
              label: 'AI Development',
              link: servicePagePath('aiAgents'),
            },
            {
              id: 'AIAgentDevelopment',
              label: 'AI Agent Development',
              link: servicePagePath('aiAgents'),
            },
            {
              id: 'machineLearning',
              label: 'Machine Learning',
              link: servicePagePath('dataMl'),
            },
            {
              id: 'dataEngineering',
              label: 'Data Engineering',
              link: servicePagePath('dataMl'),
            },
          ],
        },
        {
          id: 'maintenance',
          label: 'Maintenance',
          config: [
            {
              id: 'qa',
              label: 'Quality Assurance',
              link: servicePagePath('qualityAssurance'),
            },
            {
              id: 'productManagement',
              label: 'Product Management',
              link: servicePagePath('productManagement'),
            },
            {
              id: 'softwareMaintenanceServices',
              label: 'Software Maintenance Services',
              link: servicePagePath('softwareMaintenance'),
            },
          ],
        },
        {
          id: 'cooperationModels',
          label: 'Cooperation Models',
          config: [
            /*
             * These are engagement models, not services, so they point at
             * the cards that describe them in SectionStatsAndProcess rather
             * than at a service page. They had no link at all and were
             * falling through to the services anchor, which said nothing
             * about either of them.
             */
            {
              id: 'dedicatedTeams',
              label: 'Dedicated Teams',
              link: '/#dedicatedTeam',
            },
            {
              id: 'staffAugmentation',
              label: 'Staff Augmentation',
              link: '/#staffAugmentation',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'industries',
    label: 'Industries',
    config: {
      layout: 'listWithDescription',
      columns: 3,
      /* No per-industry pages yet — every card resolves to the contact anchor. */
      fallbackLink: '#contact-us',
      aside: {
        id: 'allIndustries',
        link: {
          label: "Let's talk",
          link: '#contact-us',
        },
        title: 'Connect with us',
        description:
          'Let’s discuss your project and explore how our expertise can support your business goals.',
        image: configPortfolio[2].menuImage,
        imageAlt: 'gitribe',
      },
      navConfig: [
        {
          id: 'ecommerce',
          label: 'E-commerce',
          description:
            'Custom, secure, and scalable e-commerce platforms with seamless integrations and mobile optimization.',
        },
        {
          id: 'finance',
          label: 'Finance',
          description:
            'Secure, scalable financial software with real-time data, analytics, and seamless payment integrations.',
        },
        {
          id: 'retail',
          label: 'Retail',
          description:
            'Custom retail software for inventory management, POS systems, and customer experiences.',
        },
        {
          id: 'healthcare',
          label: 'Healthcare',
          description:
            'Secure, compliant healthcare software for patient management, telemedicine, and integrated medical systems.',
        },
        {
          id: 'travel',
          label: 'Travel',
          description:
            'Custom travel platforms for bookings, itinerary management, and seamless customer experiences across devices.',
        },
        {
          id: 'education',
          label: 'Education',
          description:
            'Scalable e-learning platforms with interactive tools, student management, and personalized learning experiences.',
        },
      ],
    },
  },
  {
    id: 'clients',
    label: 'Clients',
    config: {
      layout: 'listWithImages',
      columns: 3,
      aside: {
        id: 'clients',
        link: {
          label: 'Our portfolio',
          link: '#portfolio',
        },
        title: 'Explore our work',
        description:
          'Discover how we’ve helped businesses with innovative and high-performing digital solutions.',
        image: configPortfolio[1].menuImage,
        imageAlt: configPortfolio[1].name,
      },
      navConfig: [
        ...featuredProjects.map(project => ({
          id: project.id,
          label: project.name,
          // Menu cards get the short line; the full description is far too
          // long for a ~210px card.
          description: project.tagline || project.description,
          image: project.menuImage,
          link: project.link || '#portfolio',
        })),
        {
          id: 'other',
          label: 'Other projects',
          config: otherProjects.map(project => ({
            id: project.id,
            label: project.shortName || project.name,
            link: project.link || '#portfolio',
          })),
        },
      ],
    },
  },
  {
    id: 'insights',
    label: 'insights',
    config: {
      layout: 'listWithImages',
      columns: 3,
      aside: {
        id: 'allInsights',
        link: {
          label: 'Visit our blog',
          link: 'https://blog.gitribe.com',
        },
        title: 'Engineering insights',
        description:
          'Deep dives, practical guides, and expert perspectives on building great software products.',
        image: configPortfolio[3].menuImage,
        imageAlt: 'gitribe blog',
      },
      /*
       * No group label: each post card carries its own category heading, so
       * a "Blog" label above them just stacked two headings on top of each
       * other. This makes the panel structurally identical to Work.
       */
      /**
       * Post cards are injected at render time from the live blog feed
       * (see MegaMenu.withLivePosts). Only the static groups below are
       * authored here — they are the fallback if the feed is unavailable.
       */
      navConfig: [
        {
          id: 'other',
          label: 'Explore topics',
          config: [
            {
              id: 'development',
              label: 'Development',
              link: 'https://blog.gitribe.com/category/development/',
            },
            {
              id: 'business-strategy',
              label: 'Business & Strategy',
              link: 'https://blog.gitribe.com/category/business-strategy/',
            },
            {
              id: 'devops-infrastructure',
              label: 'DevOps & Infrastructure',
              link: 'https://blog.gitribe.com/category/devops-infrastructure/',
            },
            {
              id: 'startups',
              label: 'Startups',
              link: 'https://blog.gitribe.com/category/startups/',
            },
          ],
        },
      ],
    },
  },
];

// Config for aboutus maybe we will add this later

// {
//   id: 'aboutUs',
//   label: 'About us',
//   config: {
//     layout: 'list',
//     columns: 3,
//     all: {
//       id: 'aboutGitribe',
//       label: 'About gtitibe',
//       link: '#aboutUs'
//     },
//     navConfig: [
//       {
//         id: 'Learn more',
//         label: 'Learn more',
//         config: [
//           {
//             id: 'About us',
//             label: 'About us',
//             link: '#aboutUs'
//           },
//           {
//             id: 'How we work',
//             label: 'How we work',
//             link: '#howWeWork'
//           },
//         ],
//       },
//       {
//         id: 'joinGitribe',
//         label: 'Join gitibe',
//         config: [
//           {
//             id: 'workingAtGitribe',
//             label: 'Working at gitribe',
//           },
//           {
//             id: 'jobOpportunities',
//             label: 'Job opportunities',
//           },
//         ],
//       },
//       {
//         id: 'contactUs',
//         label: 'Contact us',
//         config: [
//           {
//             id: 'pressOffice',
//             label: 'Press Office',
//           },
//           {
//             id: 'referGitribe',
//             label: 'Refer gitribe',
//           },
//         ],
//       },
//     ]
//   }
// }
